import { motion } from 'framer-motion';
import { Phone, Envelope, Globe, GraduationCap, Code, ChartBar, Users, Rocket, CheckCircle } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';

export default function AboutPage() {
    const { theme } = useTheme();

    const parcours = [
        { icon: GraduationCap, title: 'Ingénieur', subtitle: 'Formation', desc: 'Formation d\'ingénieur avec spécialisation en informatique et systèmes d\'information.' },
        { icon: Code, title: 'Développeur', subtitle: 'Début de carrière', desc: 'Développement d\'applications web et mobiles, maîtrise des technologies modernes (React, Node.js, Python).' },
        { icon: ChartBar, title: 'Business Analyst', subtitle: 'Évolution', desc: 'Analyse des besoins métier, rédaction de spécifications fonctionnelles.' },
        { icon: Users, title: 'Chef de Projet', subtitle: 'Management', desc: 'Pilotage de projets IT, gestion d\'équipes, coordination des parties prenantes.' },
        { icon: Rocket, title: 'Entrepreneur', subtitle: 'Aujourd\'hui', desc: 'Fondateur de DKR Consulting, accompagnement des entreprises dans leur transformation digitale.' },
    ];

    const services = [
        { title: 'Développement Web & Mobile', desc: 'Applications web modernes, sites vitrines, e-commerce, applications mobiles iOS/Android.' },
        { title: 'Conseil & Stratégie Digitale', desc: 'Audit de l\'existant, définition de la roadmap digitale, choix technologiques.' },
        { title: 'Gestion de Projet IT', desc: 'Pilotage de projets en méthode Agile ou traditionnelle, coordination des équipes.' },
        { title: 'Formation & Coaching', desc: 'Montée en compétences des équipes, ateliers pratiques, accompagnement des profils juniors.' },
    ];

    const avantages = [
        { title: 'Vision 360°', desc: 'Une compréhension globale des enjeux techniques ET business grâce à un parcours polyvalent.' },
        { title: 'Pragmatisme', desc: 'Des solutions concrètes et adaptées à votre budget, pas de sur-engineering inutile.' },
        { title: 'Engagement', desc: 'Un interlocuteur unique, impliqué de A à Z dans la réussite de votre projet.' },
    ];

    return (
        <div data-testid="about-page" className="min-h-screen">
            {/* Hero */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center"
                    >
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            À propos
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tighter mb-4">
                            DKR CONSULTING
                        </h1>
                        <p className={`max-w-2xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            De l'ingénierie au conseil, en passant par le développement et la gestion de projet : 
                            une expertise complète au service de votre transformation digitale.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Quick Links */}
            <section className={`py-8 border-b ${theme === 'dark' ? 'bg-mmd-dark-bg border-white/10' : 'bg-white border-black/10'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap justify-center gap-6 sm:gap-12">
                        <a 
                            href="https://www.dkr-consulting.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm hover:text-mmd-gold transition-colors"
                            data-testid="about-website"
                        >
                            <Globe size={18} />
                            www.dkr-consulting.com
                        </a>
                        <a 
                            href="tel:+33619446736"
                            className="flex items-center gap-2 text-sm hover:text-mmd-gold transition-colors"
                            data-testid="about-phone-fr"
                        >
                            <Phone size={18} />
                            +33 6 19 44 67 36
                        </a>
                        <a 
                            href="tel:+221778312002"
                            className="flex items-center gap-2 text-sm hover:text-mmd-gold transition-colors"
                            data-testid="about-phone-sn"
                        >
                            <Phone size={18} />
                            +221 77 831 20 02
                        </a>
                        <a 
                            href="mailto:contact@dkr-consulting.com"
                            className="flex items-center gap-2 text-sm hover:text-mmd-gold transition-colors"
                            data-testid="about-email"
                        >
                            <Envelope size={18} />
                            contact@dkr-consulting.com
                        </a>
                    </div>
                </div>
            </section>

            {/* Parcours */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl font-medium tracking-tight">Mon Parcours</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                        {parcours.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-mmd-surface-dark border border-white/10' : 'bg-white border border-black/5'}`}
                            >
                                <item.icon size={32} className={`mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`} />
                                <h3 className="font-heading font-semibold mb-1">{item.title}</h3>
                                <p className={`text-xs uppercase tracking-wide mb-3 ${theme === 'dark' ? 'text-mmd-gold/70' : 'text-mmd-navy/60'}`}>
                                    {item.subtitle}
                                </p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            Expertise
                        </p>
                        <h2 className="font-heading text-3xl font-medium tracking-tight">Mes Services</h2>
                        <p className={`mt-4 max-w-xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            Un accompagnement sur mesure pour concrétiser vos projets digitaux
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-mmd-dark-bg border border-white/10' : 'bg-mmd-light-bg border border-black/5'}`}
                            >
                                <h3 className="font-heading font-semibold text-lg mb-2">{service.title}</h3>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                                    {service.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Avantages */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl font-medium tracking-tight">Pourquoi Travailler Avec Moi ?</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {avantages.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <CheckCircle size={40} className={`mx-auto mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`} weight="fill" />
                                <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className={`rounded-2xl p-8 sm:p-12 text-center ${theme === 'dark' ? 'bg-gradient-to-br from-mmd-gold/20 to-mmd-gold/5 border border-mmd-gold/30' : 'bg-gradient-to-br from-mmd-navy/10 to-mmd-navy/5 border border-mmd-navy/20'}`}>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight mb-4">
                            Un Projet en Tête ?
                        </h2>
                        <p className={`mb-8 max-w-xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            Discutons de vos besoins et voyons ensemble comment je peux vous accompagner dans la réalisation de vos ambitions digitales.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <a
                                href="https://www.dkr-consulting.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="about-cta-website"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:-translate-y-1 ${
                                    theme === 'dark' 
                                        ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' 
                                        : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'
                                }`}
                            >
                                <Globe size={18} />
                                Visiter mon site
                            </a>
                            <a
                                href="mailto:contact@dkr-consulting.com"
                                data-testid="about-cta-email"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium border transition-all duration-200 ${
                                    theme === 'dark' 
                                        ? 'border-mmd-gold text-mmd-gold hover:bg-mmd-gold/10' 
                                        : 'border-mmd-navy text-mmd-navy hover:bg-mmd-navy/10'
                                }`}
                            >
                                <Envelope size={18} />
                                Me contacter
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
